import React, { useCallback, useContext, useMemo } from 'react'
import cn from 'clsx'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { rem } from 'polished'
import { BundleOfferPackageOptionView } from 'selectors/offerPage/bundledOfferPageSelectors'
import styled from 'styled-components'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import Caption from 'LuxKit/Typography/Caption'
import BundleAndSaveBookButton from './BundleAndSaveBookButton'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { sum } from 'lib/array/arrayUtils'
import { BundledOfferState, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getBundleValueUpView } from 'lib/bundleAndSave/getBundleValueUpView'
import { getBundlePropertiesFees } from 'lib/bundleAndSave/getBundlePropertiesFees'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import Pill from 'LuxKit/Pill'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'

const PackagePrice = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;

  & + & {
    margin-left: ${rem(12)};
  }
`

const PriceButton = styled(BundleAndSaveBookButton)`
  margin-top: ${rem(12)};
`

const PackagePrices = styled(Caption)`
  display: flex;
  position: relative;

  &.show-discount {
    margin-top: ${rem(8)};
  }

  > * {
    flex: 1 1 calc(50% - ${rem(12)});
  }

  > * + * {
    padding-left: ${rem(12)};
    border-left: 1px solid ${props => props.theme.palette.neutral.default.five};
    flex: 1 1 50%;
  }
`
const DurationCaption = styled(Caption)`
  margin-top: ${rem(8)};
`

interface Props {
  offer: App.BundleOffer;
  bundledOffersState: BundledOfferState;
  packageView: BundleOfferPackageOptionView;
  rooms: Array<App.Occupants>;
  fetching?: boolean;
  hidePartnership?: boolean;
  availableForPurchase: boolean;
  currency: string;
  defaultDepositAmountPercentage: number;
}

function BundleAndSaveOptionPriceDetails(props: Props) {
  const {
    offer,
    bundledOffersState,
    rooms,
    fetching,
    availableForPurchase,
    packageView,
  } = props

  const allPackages = useMemo(() => {
    return (packageView?.options ?? []).map(p => p.package)
  }, [packageView])

  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const checkInDates = Object.values(bundledOffersState).map((o) => o.checkInDate)
  const fromCalendarInfo = packageView?.options.every((o) => o.fromCalendarInfo)

  const hotelAvailable = packageView.hotelAvailable
  const canPurchase = availableForPurchase && packageView.capacityValid && hotelAvailable && !fetching
  const canBuyNow = !checkInDates.includes(undefined) && !!rooms && fromCalendarInfo && !fetching

  const showIncludedGuestsExceeded = useMemo(() => allPackages.some((pkg) => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer.bundledOffers[pkg.offerId])), [allPackages, rooms, offer])
  const roomPolicyDescription = useMemo(() => allPackages.find((pkg) => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer.bundledOffers[pkg.offerId]))?.roomPolicyDescription, [allPackages, rooms, offer])

  const {
    hotelTotal,
  } = packageView

  const totalPropertiesFees = getBundlePropertiesFees(offer, props.packageView)

  const { showPriceDetails, shouldShowDiscountPercent, hotelDiscountPercent, valuedUpTo } = getBundleValueUpView(offer, packageView)

  const duration = useMemo(() => {
    return sum(allPackages, (p) => p.duration)
  }, [allPackages])

  const onSubmit = useCallback(() => {
    if (!canBuyNow) {
      trackEvent('CHECK_AVAILABILITY_PRESSED')
    } else {
      trackEvent('BOOK_NOW_PRESSED')
    }
  }, [canBuyNow, trackEvent])

  return (<div>
    {!isBundleAndSaveV2Enabled() && shouldShowDiscountPercent && canPurchase && <PriceRowValueDiscount
      type="estimate"
      discountPercentage={hotelDiscountPercent}
    />}
    <PackagePrices variant="medium" className={cn({ 'show-discount': shouldShowDiscountPercent })}>
      {!offer.bundledWithFlightsOnly &&
        <PackagePrice data-testid="pricing">
          {canPurchase && <div>
            {isBundleAndSaveV2Enabled() &&
              <>
                <DurationCaption variant="medium">
                  Total for {pluralizeToString('night', duration)}, 2 hotels {!checkInDates[0]}
                </DurationCaption>
                <Heading variant="heading4">
                  <FormatCurrency value={hotelTotal} format="roundedDollar" />
                </Heading>
              </>}
            {!isBundleAndSaveV2Enabled() &&
              <>
                <PriceRowPriceCaption>
                  {pluralizeToString('night', duration)}, 2 hotels {!checkInDates[0] && 'from'}
                </PriceRowPriceCaption>
                <PriceRowPrice
                  size="S"
                  price={hotelTotal}
                  saleUnit="total"
                />
              </>}
            <PriceRowTaxesAndFees />

            {isBundleAndSaveV2Enabled() &&
              <ValueWrapper variant="medium" weight="bold">
                <Caption variant="large">
                  Valued up to <FormatCurrency value={valuedUpTo} />
                </Caption>
                {shouldShowDiscountPercent && <Pill kind="primary" variant="success">{hotelDiscountPercent}%</Pill>}
              </ValueWrapper>}
            {showIncludedGuestsExceeded && (
              <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={roomPolicyDescription}
              />
            )}
            {showPriceDetails &&
              <OfferPriceDetailsRow
                trigger="price-row"
                triggerSize="M"
                offerPage
                offer={offer}
                duration={duration}
                propertyFees={props.packageView.propertyFees}
                propertiesFees={totalPropertiesFees}
                hotelPrice={props.packageView.hotelPrice}
                rooms={rooms}
                extraGuestMessage={showIncludedGuestsExceeded ? roomPolicyDescription : ''}
                cta={
                  <BundleAndSaveBookButton
                    type="submit"
                    disabled={fetching}
                    packageView={props.packageView}
                    onSubmit={onSubmit}
                  >
                    {canBuyNow ? 'Book now' : 'Check availability'}
                  </BundleAndSaveBookButton>
                }
              />}
          </div>}

          {availableForPurchase && canPurchase && <PriceButton
            data-testid="HotelBookingBtn"
            type="submit"
            disabled={fetching}
            packageView={props.packageView}
            onSubmit={onSubmit}
          >
            {canBuyNow ? 'Book now' : 'Check availability'}
          </PriceButton>}
          {availableForPurchase && !canPurchase && (
            <div>
              {hotelAvailable && !packageView.capacityValid && (
                <Caption variant="large" align="center">
                  Max number of guests exceeded.
                  <br />
                </Caption>
              )}

              {!hotelAvailable && (
                <BodyText variant="medium">
                  Sold out for your selected dates.
                </BodyText>
              )}
            </div>
          )}
        </PackagePrice>}

      <LoadingIndicator visible={fetching} floating opaque />
    </PackagePrices>
  </div>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  currency: appState.geo.currentCurrency,
  bundledOffersState: state.bundledOffers,
  rooms: state.rooms,
  offer: state.offer,
  isLoggedIn: isLoggedInSelector(appState),
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}), BundleAndSaveOptionPriceDetails)
